import { c as _c } from "react/compiler-runtime";
import type { HTMLAttributes, ReactNode } from 'react';
import { Badge as ShadcnBadge } from '@/components/ui/badge';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Public Badge wrapper — restricted variant set (default | secondary |
// destructive | outline). `ghost` and `link` are excluded — zero callsite
// usage and no clear design role. The underlying shadcn primitive keeps its
// broader variant set as the cva source-of-truth.
// ---------------------------------------------------------------------------

export type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';
export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  variant?: BadgeVariant | undefined;
  /** Layout passthrough only (positioning, alignment). Visual-token className
   *  (`bg-*`, `text-*`, `border-*`, `rounded-*`) is flagged by FQ-110 — use a
   *  variant or add `// ALLOW-RAW-BADGE-STYLE: <reason>` for genuine one-offs. */
  className?: string | undefined;
  children?: ReactNode | undefined;
}

// Morfi semantic tokens (replace stock's text-xs + rounded-full).
// `rounded-pill` couples Badge to the same capsule token as sort/filter pills
// so a future shape change is a single token edit.
const BADGE_MORFI_TOKENS = 'rounded-pill text-caption' as const;
export function Badge(t0) {
  const $ = _c(12);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      variant: t1,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const variant = t1 === undefined ? "default" : t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn(BADGE_MORFI_TOKENS, className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== props || $[9] !== t2 || $[10] !== variant) {
    t3 = <ShadcnBadge variant={variant} className={t2} {...props}>{children}</ShadcnBadge>;
    $[7] = children;
    $[8] = props;
    $[9] = t2;
    $[10] = variant;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}